import React from 'react';
import { SelectOption } from 'global';
import { useFormContext } from 'react-hook-form';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Box, Button, Checkbox, Stack } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { AutocompleteField, InputField, RadioGroupField } from 'components/form';
import { FormSectionHeader, FormSectionHeaderProps } from 'components/shared/FormSectionHeader';

import { lenderDeskingPanelMapper } from '../../../mappers';
import { EmailOptions, LenderDeskingPanelFormValues } from '../../../types';

type ActionEventType = 'saveAsDraft' | 'sendToDealer' | 'cancelChanges' | 'sendToApplicant';
type ActionEvent<Type> = { type: Type };

interface Action<Type> {
  isLoading?: boolean;
  isDisabled?: boolean;
  fn: (event: ActionEvent<Type>) => void;
}

interface Actions {
  saveAsDraft: Action<ActionEventType>;
  sendToDealer: Action<ActionEventType>;
  cancelChanges: Action<ActionEventType>;
  sendToApplicant: Action<ActionEventType>;
}

interface Props<Value extends string> extends FormSectionHeaderProps<Value> {
  actions: Actions;
  availableEmailOptions: EmailOptions[];
}

export const sendViaOptions: SelectOption[] = [
  { label: 'Email', value: 'Email' },
  { label: 'Text', value: 'Text' },
  { label: 'Text and email', value: 'TextAndEmail' },
];

export const ActionsSection = <Value extends string>({
  actions,
  availableEmailOptions,
  ...headerProps
}: Props<Value>) => {
  const { control } = useFormContext<LenderDeskingPanelFormValues>();

  return (
    <Stack gap="1rem">
      <FormSectionHeader {...headerProps} />
      <Box className="row">
        <Box>
          <InputField
            control={control}
            name="submission.noteToBuyer"
            label="Add a note to the buyer"
            inputProps={{
              minRows: 3,
              maxRows: 3,
              multiline: true,
              variant: 'outlined',
              placeholder: 'Add a note to the buyer',
            }}
          />

          <RadioGroupField row control={control} name="actions.sendVia" options={sendViaOptions} />
        </Box>

        <Box>
          <AutocompleteField<any, true, any>
            multiple
            size="small"
            limitTags={3}
            control={control}
            disableCloseOnSelect
            name="actions.notifyUsers"
            options={availableEmailOptions}
            groupBy={option => option.group}
            getOptionLabel={lenderDeskingPanelMapper.processNotifyLabel}
            textFieldProps={{ label: 'Notify users', variant: 'outlined' }}
            isOptionEqualToValue={(option, value) => value && option.email === value?.email}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;

              return (
                <li key={key} {...optionProps}>
                  <Checkbox
                    checked={selected}
                    style={{ marginRight: 8 }}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  />
                  {lenderDeskingPanelMapper.processNotifyLabel(option, true)}
                </li>
              );
            }}
          />
        </Box>
      </Box>

      <Stack direction="row" gap="1rem">
        <Button
          loading={actions.sendToDealer.isLoading ?? false}
          onClick={() => actions.sendToDealer.fn({ type: 'sendToDealer' })}
        >
          Send to Dealer
        </Button>

        <Button
          loading={actions.sendToDealer.isLoading ?? false}
          onClick={() => actions.sendToApplicant.fn({ type: 'sendToApplicant' })}
        >
          Send to Applicant
        </Button>

        <Button
          variant="text"
          loading={actions.sendToDealer.isLoading ?? false}
          onClick={() => actions.saveAsDraft.fn({ type: 'saveAsDraft' })}
        >
          Save as draft
        </Button>

        <Button
          color="error"
          variant="text"
          loading={actions.sendToDealer.isLoading ?? false}
          disabled={actions.sendToDealer.isDisabled ?? false}
          onClick={() => actions.cancelChanges.fn({ type: 'cancelChanges' })}
        >
          Cancel changes
        </Button>
      </Stack>
    </Stack>
  );
};
